import React, { useState, useEffect } from 'react';
import { StudyGuideApi } from '../../services/api/StudyGuideApi';

/**
 * TopicDetailModal — Slide-over panel showing detailed mastery info for a single concept.
 * Displays mastery score, evidence count, confidence, concept description, and action buttons.
 */
export default function TopicDetailModal({ slug, endeavorId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    StudyGuideApi.getTopicDetail(slug, endeavorId)
      .then(setDetail)
      .catch(err => console.error('Failed to load topic detail:', err))
      .finally(() => setLoading(false));
  }, [slug, endeavorId]);

  if (!slug) return null;

  const mastery = detail?.mastery;
  const conceptInfo = detail?.conceptInfo;
  const masteryScore = mastery?.masteryScore ?? 0;
  const confidence = mastery?.confidence ?? 0;
  const evidenceCount = mastery?.evidenceCount ?? 0;

  const getMasteryColor = (score) => {
    if (score >= 80) return '#28a745';
    if (score >= 50) return '#ffc107';
    return '#dc3545';
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={panelStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <h3 style={{ margin: '0 0 .25rem 0', fontSize: '1.1rem' }}>
              {conceptInfo?.conceptName || slug.replace(/-/g, ' ')}
            </h3>
            {conceptInfo?.description && (
              <p style={{ margin: 0, fontSize: '.82rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                {conceptInfo.description}
              </p>
            )}
          </div>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>

        {loading ? (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem 0' }}>Loading...</p>
        ) : (
          <>
            {/* Mastery gauge */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{
                width: '100px', height: '100px', borderRadius: '50%',
                border: `6px solid ${getMasteryColor(masteryScore)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto .75rem auto',
                fontSize: '1.5rem', fontWeight: 700,
                color: getMasteryColor(masteryScore)
              }}>
                {Number(masteryScore).toFixed(0)}%
              </div>
              <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>Mastery Score</div>
            </div>

            {/* Stats row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '.75rem', marginBottom: '1.5rem' }}>
              <StatBox label="Confidence" value={`${Number(confidence).toFixed(0)}%`} />
              <StatBox label="Evidence Points" value={evidenceCount} />
              <StatBox label="Documents" value={detail?.documentCount ?? 0} />
            </div>

            {/* Last quiz score */}
            {mastery?.lastQuizScore != null && (
              <div style={infoRowStyle}>
                <span style={{ color: 'var(--text-muted)', fontSize: '.82rem' }}>Last Quiz Score</span>
                <span style={{ fontWeight: 600, fontSize: '.9rem' }}>
                  {Number(mastery.lastQuizScore).toFixed(0)}%
                </span>
              </div>
            )}

            {/* Last updated */}
            {mastery?.lastUpdated && (
              <div style={infoRowStyle}>
                <span style={{ color: 'var(--text-muted)', fontSize: '.82rem' }}>Last Updated</span>
                <span style={{ fontSize: '.82rem' }}>
                  {new Date(mastery.lastUpdated).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </span>
              </div>
            )}

            {/* Concept importance */}
            {conceptInfo?.importance != null && (
              <div style={infoRowStyle}>
                <span style={{ color: 'var(--text-muted)', fontSize: '.82rem' }}>Importance</span>
                <span style={{ fontSize: '.82rem' }}>
                  {'★'.repeat(Math.min(conceptInfo.importance, 10)).padEnd(10, '☆').substring(0, 10)}
                  {' '}{conceptInfo.importance}/10
                </span>
              </div>
            )}

            {/* Action buttons */}
            <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <ActionButton
                icon="🧪"
                label="Generate Focused Quiz"
                sublabel="Test your knowledge on this topic"
                onClick={() => window.location.href = `/ai-tools?action=quiz&concept=${slug}`}
              />
              <ActionButton
                icon="🃏"
                label="Review Flashcards"
                sublabel="Practice with spaced repetition"
                onClick={() => window.location.href = `/ai-tools?action=flashcards&concept=${slug}`}
              />
              <ActionButton
                icon="💬"
                label="Ask Tutor About This"
                sublabel="Get help clarifying concepts"
                onClick={() => window.location.href = `/ai-tools?action=chat&concept=${slug}`}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div style={{
      padding: '.75rem', textAlign: 'center',
      background: 'rgba(0,123,255,.05)', borderRadius: '8px'
    }}>
      <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginTop: '.15rem' }}>{label}</div>
    </div>
  );
}

function ActionButton({ icon, label, sublabel, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '.75rem',
        padding: '.75rem 1rem', background: 'var(--bg-card)',
        border: '1px solid var(--border-light)', borderRadius: '8px',
        cursor: 'pointer', textAlign: 'left', width: '100%'
      }}
    >
      <span style={{ fontSize: '1.25rem' }}>{icon}</span>
      <div>
        <div style={{ fontWeight: 600, fontSize: '.85rem' }}>{label}</div>
        {sublabel && <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{sublabel}</div>}
      </div>
    </button>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────

const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,.45)', zIndex: 1000,
  display: 'flex', justifyContent: 'flex-end'
};

const panelStyle = {
  width: '400px', maxWidth: '90vw', height: '100vh',
  background: 'var(--bg-card, #fff)', padding: '1.5rem',
  overflowY: 'auto', boxShadow: '-4px 0 16px rgba(0,0,0,.15)'
};

const closeBtnStyle = {
  background: 'none', border: 'none', fontSize: '1.1rem',
  cursor: 'pointer', padding: '.25rem', color: 'var(--text-muted)'
};

const infoRowStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '.5rem 0', borderBottom: '1px solid var(--border-light, #eee)'
};
